import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import Sidebar from '../components/admin/Sidebar';
import TopNavbar from '../components/admin/TopNavbar';
import CouponStatsCards from '../components/admin/coupons/CouponStatsCards';
import CouponFilterTabs from '../components/admin/coupons/CouponFilterTabs';
import CouponSearchBar from '../components/admin/coupons/CouponSearchBar';
import CouponCard from '../components/admin/coupons/CouponCard';
import CreateEditCouponModal from '../components/admin/coupons/CreateEditCouponModal';
import ViewTermsModal from '../components/admin/coupons/ViewTermsModal';
import CouponAnalyticsModal from '../components/admin/coupons/CouponAnalyticsModal';
import SuccessToast from '../components/common/SuccessToast';
import './CouponsManagement.css';

const CouponsManagement = () => {
  const navigate = useNavigate();
  const [adminSession, setAdminSession] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState('Active');
  const [searchQuery, setSearchQuery] = useState('');
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [selectedCouponForTerms, setSelectedCouponForTerms] = useState(null);
  const [selectedCouponForAnalytics, setSelectedCouponForAnalytics] = useState(null);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [stats, setStats] = useState({
    active: 0,
    totalUsed: 0,
    discountGiven: 0,
    bestPerformer: ''
  });

  useEffect(() => {
    const session = localStorage.getItem('adminSession') || sessionStorage.getItem('adminSession');
    if (!session) {
      navigate('/admin');
      return;
    }
    setAdminSession(JSON.parse(session));
  }, [navigate]);

  useEffect(() => {
    fetchCoupons();
  }, [activeFilter, searchQuery]);

  useEffect(() => {
    // Calculate stats from current coupons
    if (coupons.length > 0) {
      const now = new Date();
      const activeCount = coupons.filter(c => 
        c.isActive && new Date(c.validTill) >= now
      ).length;
      const totalUsed = coupons.reduce((sum, c) => sum + (c.totalRedemptions || c.timesUsed || 0), 0);
      const discountGiven = coupons.reduce((sum, c) => sum + (c.revenueImpact || 0), 0);
      const bestCoupon = coupons.reduce((best, c) => 
        (c.totalRedemptions || c.timesUsed || 0) > (best.totalRedemptions || best.timesUsed || 0) ? c : best,
        coupons[0]
      );

      setStats({
        active: activeCount,
        totalUsed: totalUsed,
        discountGiven: discountGiven,
        bestPerformer: bestCoupon?.code || ''
      });
    } else {
      // Fetch from Supabase if no local coupons
      fetchStats();
    }
  }, [coupons]);

  const fetchStats = async () => {
    try {
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      if (!supabaseUrl || supabaseUrl === 'YOUR_SUPABASE_URL') {
        // Stats will be calculated from coupons in useEffect
        return;
      }

      // Fetch real stats from Supabase
      const now = new Date().toISOString();
      
      // Get active coupons count
      const { count: activeCount } = await supabase
        .from('coupons')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true)
        .gte('valid_till', now);

      // Get total used and revenue impact
      const { data: allCoupons } = await supabase
        .from('coupons')
        .select('times_used, total_redemptions, revenue_impact, code');

      if (allCoupons && allCoupons.length > 0) {
        const totalUsed = allCoupons.reduce((sum, c) => sum + (c.total_redemptions || c.times_used || 0), 0);
        const discountGiven = allCoupons.reduce((sum, c) => sum + (c.revenue_impact || 0), 0);
        
        // Find best performer
        const bestCoupon = allCoupons.reduce((best, c) => 
          (c.total_redemptions || c.times_used || 0) > (best.total_redemptions || best.times_used || 0) ? c : best,
          allCoupons[0]
        );

        setStats({
          active: activeCount || 0,
          totalUsed: totalUsed,
          discountGiven: discountGiven,
          bestPerformer: bestCoupon?.code || ''
        });
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      if (!supabaseUrl || supabaseUrl === 'YOUR_SUPABASE_URL') {
        // Demo mode - return sample coupons
        const demoCoupons = generateDemoCoupons();
        setCoupons(demoCoupons);
        setLoading(false);
        return;
      }

      // Fetch from Supabase
      const { data, error } = await supabase
        .from('coupons')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching coupons:', error);
        setCoupons([]);
        setLoading(false);
        return;
      }

      // Transform database data to component format (snake_case to camelCase)
      const transformedCoupons = (data || []).map(coupon => ({
        id: coupon.id,
        code: coupon.code,
        description: coupon.description || '',
        discountType: coupon.discount_type,
        discountValue: coupon.discount_value,
        maxDiscount: coupon.max_discount,
        minOrderAmount: coupon.min_order_amount,
        validFrom: coupon.valid_from,
        validTill: coupon.valid_till,
        usageLimit: coupon.usage_limit,
        perUserLimit: coupon.per_user_limit || false,
        applicableTo: coupon.applicable_to || 'all',
        applicableCategories: coupon.applicable_categories || [],
        applicableItems: coupon.applicable_items || [],
        terms: coupon.terms || '',
        isActive: coupon.is_active !== undefined ? coupon.is_active : true,
        timesUsed: coupon.times_used || 0,
        totalRedemptions: coupon.total_redemptions || coupon.times_used || 0,
        revenueImpact: parseFloat(coupon.revenue_impact || 0)
      }));

      setCoupons(transformedCoupons);
    } catch (error) {
      console.error('Error fetching coupons:', error);
      setCoupons([]);
    } finally {
      setLoading(false);
    }
  };

  const generateDemoCoupons = () => {
    const now = new Date();
    const future = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    const past = new Date(now.getTime() - 10 * 24 * 60 * 60 * 1000);

    return [
      {
        id: '1',
        code: 'FIRST50',
        description: '50% off on first order',
        discountType: 'percentage',
        discountValue: 50,
        maxDiscount: 200,
        minOrderAmount: 500,
        validFrom: '2024-12-01',
        validTill: '2024-12-31',
        usageLimit: 100,
        perUserLimit: true,
        applicableTo: 'all',
        terms: 'Valid for first-time customers only. Cannot be combined with other offers.',
        isActive: true,
        timesUsed: 45,
        totalRedemptions: 45,
        revenueImpact: 9000
      },
      {
        id: '2',
        code: 'SUMMER20',
        description: 'PKR 200 off on orders above PKR 1000',
        discountType: 'fixed',
        discountValue: 200,
        minOrderAmount: 1000,
        validFrom: '2024-12-15',
        validTill: future.toISOString().split('T')[0],
        usageLimit: null,
        perUserLimit: false,
        applicableTo: 'all',
        terms: 'Valid on all items. Minimum order value applies.',
        isActive: true,
        timesUsed: 78,
        totalRedemptions: 78,
        revenueImpact: 15600
      },
      {
        id: '3',
        code: 'WEEKEND',
        description: '30% off on weekends',
        discountType: 'percentage',
        discountValue: 30,
        maxDiscount: 150,
        minOrderAmount: 300,
        validFrom: past.toISOString().split('T')[0],
        validTill: past.toISOString().split('T')[0],
        usageLimit: 50,
        perUserLimit: true,
        applicableTo: 'all',
        terms: 'Valid only on Saturdays and Sundays.',
        isActive: false,
        timesUsed: 32,
        totalRedemptions: 32,
        revenueImpact: 4800
      }
    ];
  };

  const handleCreateCoupon = () => {
    setEditingCoupon(null);
    setIsCreateModalOpen(true);
  };

  const handleEditCoupon = (coupon) => {
    setEditingCoupon(coupon);
    setIsCreateModalOpen(true);
  };

  const handleSaveCoupon = async (couponData) => {
    try {
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      if (!supabaseUrl || supabaseUrl === 'YOUR_SUPABASE_URL') {
        // Demo mode
        if (editingCoupon) {
          setCoupons(prev => prev.map(c => c.id === editingCoupon.id ? { ...c, ...couponData } : c));
        } else {
          const newCoupon = {
            ...couponData,
            id: String(coupons.length + 1),
            timesUsed: 0,
            totalRedemptions: 0,
            revenueImpact: 0
          };
          setCoupons(prev => [...prev, newCoupon]);
        }
        setIsCreateModalOpen(false);
        setEditingCoupon(null);
        return;
      }

      // Transform data to match database schema (camelCase to snake_case)
      // Format dates properly - add time if only date is provided
      const formatDateForDB = (dateString) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        // If only date (no time), set to start of day for valid_from, end of day for valid_till
        if (dateString.length === 10) {
          date.setHours(0, 0, 0, 0);
        }
        return date.toISOString();
      };

      const formatDateTillForDB = (dateString) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        // For valid_till, set to end of day
        if (dateString.length === 10) {
          date.setHours(23, 59, 59, 999);
        }
        return date.toISOString();
      };

      const dbData = {
        code: (couponData.code || '').toUpperCase().trim(),
        description: couponData.description || null,
        discount_type: couponData.discountType,
        discount_value: parseFloat(couponData.discountValue),
        max_discount: couponData.maxDiscount ? parseFloat(couponData.maxDiscount) : null,
        min_order_amount: couponData.minOrderAmount ? parseFloat(couponData.minOrderAmount) : null,
        valid_from: formatDateForDB(couponData.validFrom),
        valid_till: formatDateTillForDB(couponData.validTill),
        usage_limit: couponData.usageLimit ? parseInt(couponData.usageLimit) : null,
        per_user_limit: couponData.perUserLimit || false,
        applicable_to: couponData.applicableTo || 'all',
        applicable_categories: couponData.applicableTo === 'categories' && couponData.applicableCategories && couponData.applicableCategories.length > 0
          ? couponData.applicableCategories 
          : null,
        terms: couponData.terms || null,
        is_active: couponData.isActive !== undefined ? couponData.isActive : true
      };

      console.log('Saving coupon data to Supabase:', dbData);

      // Save to Supabase
      if (editingCoupon) {
        const { error } = await supabase
          .from('coupons')
          .update(dbData)
          .eq('id', editingCoupon.id);
        if (error) {
          console.error('Supabase error:', error);
          throw error;
        }
      } else {
        const { error } = await supabase
          .from('coupons')
          .insert([dbData]);
        if (error) {
          console.error('Supabase error:', error);
          throw error;
        }
      }

      fetchCoupons();
      fetchStats(); // Refresh stats after saving
      setIsCreateModalOpen(false);
      setEditingCoupon(null);
      setSuccessMessage(editingCoupon ? 'Coupon updated successfully!' : 'Coupon created successfully!');
      setShowSuccessToast(true);
    } catch (error) {
      console.error('Error saving coupon:', error);
      const errorMessage = error.message || 'Failed to save coupon. Please try again.';
      console.error('Full error details:', JSON.stringify(error, null, 2));
      setSuccessMessage(errorMessage);
      setShowSuccessToast(true);
    }
  };

  const handleDeleteCoupon = (couponId) => {
    if (window.confirm('Are you sure you want to delete this coupon?')) {
      setCoupons(prev => prev.filter(c => c.id !== couponId));
    }
  };

  const handleToggleActive = async (coupon) => {
    try {
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      if (!supabaseUrl || supabaseUrl === 'YOUR_SUPABASE_URL') {
        setCoupons(prev => prev.map(c => 
          c.id === coupon.id ? { ...c, isActive: !c.isActive } : c
        ));
        return;
      }

      const { error } = await supabase
        .from('coupons')
        .update({ is_active: !coupon.isActive })
        .eq('id', coupon.id);

      if (error) throw error;
      fetchCoupons();
    } catch (error) {
      console.error('Error toggling coupon status:', error);
    }
  };

  // Filter coupons
  const filteredCoupons = coupons.filter(coupon => {
    if (activeFilter === 'All') return true;
    if (activeFilter === 'Active') return coupon.isActive && new Date(coupon.validTill) >= new Date();
    if (activeFilter === 'Scheduled') return !coupon.isActive && new Date(coupon.validFrom) > new Date();
    if (activeFilter === 'Expired') return new Date(coupon.validTill) < new Date();
    return true;
  });

  // Apply search
  const searchedCoupons = filteredCoupons.filter(coupon => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return coupon.code.toLowerCase().includes(query) ||
           coupon.description.toLowerCase().includes(query);
  });

  if (!adminSession) {
    return <div className="loading-screen">Loading...</div>;
  }

  return (
    <div className="coupons-management">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="coupons-main">
        <TopNavbar 
          onMenuClick={() => setSidebarOpen(true)}
          adminName={adminSession.username || adminSession.email}
        />
        
        <main className="coupons-content">
          <div className="page-header">
            <div>
              <h1 className="page-title">Coupons & Offers</h1>
              <nav className="breadcrumb">
                <span>Dashboard</span>
                <span className="breadcrumb-separator">/</span>
                <span>Coupons</span>
              </nav>
            </div>
            <button className="create-coupon-btn" onClick={handleCreateCoupon}>
              ➕ Create New Coupon
            </button>
          </div>

          <CouponStatsCards stats={stats} />

          <CouponFilterTabs
            activeFilter={activeFilter}
            onFilterChange={setActiveFilter}
          />

          <CouponSearchBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
          />

          {loading ? (
            <div className="loading-skeleton">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="skeleton-card" />
              ))}
            </div>
          ) : searchedCoupons.length === 0 ? (
            <div className="empty-state">
              <span className="empty-icon">🎟️</span>
              <p className="empty-text">No coupons created yet</p>
              <p className="empty-subtext">Create your first coupon to attract customers</p>
              <button className="empty-create-btn" onClick={handleCreateCoupon}>
                Create Coupon
              </button>
            </div>
          ) : (
            <div className="coupons-grid">
              {searchedCoupons.map(coupon => (
                <CouponCard
                  key={coupon.id}
                  coupon={coupon}
                  onEdit={() => handleEditCoupon(coupon)}
                  onDelete={() => handleDeleteCoupon(coupon.id)}
                  onToggleActive={() => handleToggleActive(coupon)}
                  onViewTerms={() => setSelectedCouponForTerms(coupon)}
                  onViewAnalytics={() => setSelectedCouponForAnalytics(coupon)}
                />
              ))}
            </div>
          )}

          {isCreateModalOpen && (
            <CreateEditCouponModal
              coupon={editingCoupon}
              onClose={() => {
                setIsCreateModalOpen(false);
                setEditingCoupon(null);
              }}
              onSave={handleSaveCoupon}
            />
          )}

          {selectedCouponForTerms && (
            <ViewTermsModal
              coupon={selectedCouponForTerms}
              onClose={() => setSelectedCouponForTerms(null)}
            />
          )}

          {selectedCouponForAnalytics && (
            <CouponAnalyticsModal
              coupon={selectedCouponForAnalytics}
              onClose={() => setSelectedCouponForAnalytics(null)}
            />
          )}

          {showSuccessToast && (
            <SuccessToast
              message={successMessage}
              onClose={() => setShowSuccessToast(false)}
              duration={3000}
              type={successMessage.includes('Failed') ? 'error' : 'success'}
            />
          )}
        </main>
      </div>
    </div>
  );
};

export default CouponsManagement;

